import type { BusinessTypeCode } from '../../shell/session/domain/businessProfile';
import { supabase } from '../../shared/supabase/supabaseClient';

/** Código ERP del rubro "Otros" en el picker de onboarding / cambiar rubro. */
export const OTHER_RUBRO_PICKER_CODE = 'general_retail' as const;

export type OtherRubroOperationTemplate = 'retail' | 'appointments' | 'workshop' | 'food';

export type OtherRubroSupportSource =
  | 'onboarding_other_rubro'
  | 'additional_org_other_rubro'
  | 'change_business_type_other';

export const OTHER_RUBRO_TEMPLATE_OPTIONS: ReadonlyArray<{
  id: OtherRubroOperationTemplate;
  label: string;
  hint: string;
}> = [
  { id: 'retail', label: 'Vendo productos', hint: 'Mostrador con stock y catálogo.' },
  { id: 'appointments', label: 'Doy turnos', hint: 'Agenda y cobro de servicios.' },
  { id: 'workshop', label: 'Reparo o arreglo', hint: 'Mano de obra y repuestos.' },
  { id: 'food', label: 'Vendo comida', hint: 'Comandas y cobro al paso.' },
];

export function isOtherRubroPickerCode(code: string | null | undefined): boolean {
  return code === OTHER_RUBRO_PICKER_CODE;
}

export function resolveBusinessTypeFromOtherTemplate(
  template: OtherRubroOperationTemplate,
): BusinessTypeCode {
  switch (template) {
    case 'retail':
      return 'general_retail';
    case 'appointments':
      return 'services_beauty';
    case 'workshop':
      return 'services_workshop';
    case 'food':
      return 'quick_service_food';
    default:
      return 'general_retail';
  }
}

export function otherRubroTemplateLabel(template: OtherRubroOperationTemplate): string {
  return OTHER_RUBRO_TEMPLATE_OPTIONS.find((o) => o.id === template)?.label ?? template;
}

export function organizationDisplayRubroLabel(params: {
  requestedVerticalLabel?: string | null;
  businessTypeName?: string | null;
  businessTypeCode?: string | null;
}): string {
  const label = params.requestedVerticalLabel?.trim();
  if (label) return label;
  return params.businessTypeName?.trim() || params.businessTypeCode?.trim() || 'Rubro';
}

/**
 * Resuelve el business_type_code desde el texto libre del onboarding "Otros".
 * Llama a la RPC `resolve_business_type_from_label` que busca aliases en el label.
 * Fallback a `resolveBusinessTypeFromOtherTemplate` si la RPC no existe o falla.
 */
export async function resolveBusinessTypeFromLabelAndTemplate(
  label: string,
  template: OtherRubroOperationTemplate,
): Promise<BusinessTypeCode> {
  const fallback = resolveBusinessTypeFromOtherTemplate(template);
  const trimmed = label?.trim();
  if (!trimmed) return fallback;
  try {
    const { data, error } = await supabase.rpc('resolve_business_type_from_label', {
      p_label: trimmed,
      p_template: template,
    });
    if (error || !data) return fallback;
    return data as BusinessTypeCode;
  } catch {
    return fallback;
  }
}
